import './Tooltip.css'

import { type Placement, useMergeRefs } from '@floating-ui/react'
import {
  cloneElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefObject,
  useId,
  useRef,
} from 'react'

import { useIsOverflowing } from '#design-system/hooks/useIsOverflowing'

interface BaseTooltipProps {
  /** The element the tooltip describes. Must be a single React element
   * so we can clone it with a merged ref, `aria-describedby`, and an inline `anchor-name` style. */
  children: ReactElement
  /** Side of the anchor the tooltip prefers.
   * Auto-flips on overflow via CSS `position-try-fallbacks`. */
  placement?: Placement
  /** Override the anchor target. Default: the cloned child.
   * Use when the child renders multiple top-level elements (a fragment)
   * or when a nested element should anchor. */
  targetRef?: RefObject<HTMLElement | null>
  /** Element to measure for truncation when `onlyShowIfReferenceTruncated` is true.
   * Defaults to `targetRef ?? cloned child`. */
  isTruncatedElementRef?: RefObject<HTMLElement | null>
  /** When true, suppress the tooltip unless the reference's content is clipped
   * (`scrollWidth > clientWidth`).
   * Costs one ResizeObserver per instance when enabled; zero cost when false / unset. */
  onlyShowIfReferenceTruncated?: boolean
}

/** A tooltip may either display a simple title string or a custom component, but not both.
 *
 * Both sides accept falsy values (`undefined`, `''`, `null`) so consumers can wrap unconditionally;
 * a falsy tooltip renders no host wrapper and no tooltip DOM.
 *
 * Performance note: there's a small performance overhead to wrapping even if not rendering.
 * This is OK in the vast majority of cases for simplest DX, but consider wrapping conditionally
 * if the wrapped element is expected to render 100's+ of times at once. */
type TooltipContent = { title?: string; content?: never } | { content?: ReactNode; title?: never }

export type TooltipProps = BaseTooltipProps & TooltipContent

/** Props a wrapper that clones the Tooltip (e.g. FlyOut's trigger) injects. Not public API */
type InjectedProps = Record<string, unknown> & {
  ref?: Ref<HTMLElement>
  style?: CSSProperties
}

interface ClonedChildProps {
  ref?: Ref<HTMLElement>
  style?: CSSProperties | undefined
}
type ClonedChild = ReactElement<ClonedChildProps>

export const Tooltip = (props: TooltipProps): ReactElement => {
  const {
    children,
    placement = 'top',
    targetRef,
    isTruncatedElementRef,
    onlyShowIfReferenceTruncated = false,
    title,
    content,
    ...rest
  } = props
  const { ref: injectedRef, ...injected } = rest as InjectedProps

  // useId() returns strings like ":r1:" — strip colons so the value is a valid CSS dashed-ident
  // for anchor-name / position-anchor.
  const reactId = useId()
  const tooltipId = `doss-tooltip-${reactId.replace(/:/g, '-')}`
  const anchorName = `--${tooltipId}`

  const childRef = useRef<HTMLElement | null>(null)
  const child = children as ClonedChild

  const hasContent = Boolean(title) || Boolean(content)

  // With `targetRef`, truncation is measured on that element instead of the child
  const mergedRef = useMergeRefs([child.props.ref, injectedRef, targetRef ? null : childRef])

  // Rules of Hooks: call the hook unconditionally.
  // The `enabled` flag gates observer creation inside the hook, so when truncation mode is off
  // (the common case) we pay zero observer cost — one useState slot and one no-op useLayoutEffect.
  const truncationRef = isTruncatedElementRef ?? targetRef ?? childRef
  const isOverflowing = useIsOverflowing(truncationRef, onlyShowIfReferenceTruncated)
  // Default to suppressed until measurement has confirmed overflow.
  // `isOverflowing` is `undefined` on the first render (before useLayoutEffect runs) —
  // without this guard the tooltip would flash on hover before the suppression kicks in.
  const suppressDueToTruncation = onlyShowIfReferenceTruncated && isOverflowing !== true

  // Injected props override the child's, as if the wrapper had cloned the child directly.
  // A falsy title/content gets no anchor or description, only the injected props.
  // oxlint-disable-next-line react-hooks/refs
  const clonedChild = cloneElement(child, {
    ...injected,
    ref: mergedRef,
    ...(hasContent && { 'aria-describedby': tooltipId }),
    style: { ...child.props.style, ...injected.style, ...(hasContent && { anchorName }) },
  })

  // Falsy title/content → skip the host wrapper and the tooltip DOM entirely
  if (!hasContent) {
    return clonedChild
  }

  return (
    <span className="doss-tooltip-host" style={{ display: 'contents' }}>
      {clonedChild}
      <div
        className="doss-tooltip"
        data-placement={placement}
        data-truncated={suppressDueToTruncation ? 'false' : undefined}
        id={tooltipId}
        role="tooltip"
        style={{ positionAnchor: anchorName }}
      >
        {title ?? content}
      </div>
    </span>
  )
}
